import BatFlapping from "components/BatFlapping";
import desktopBackgroundZoomed from "art/desktop_background_zoomed.png";
import CharacterInfo from "components/CharacterInfo";
import Intro from "components/Intro";
import SessionNameModal from "components/SessionNameModal";
import { SelectableLinkContainer } from "components/Link";
import Day from "containers/Day";
import Expired from "containers/Expired";
import EndGame from "containers/EndGame";
import LoginForm from "containers/LoginForm";
import Morning from "containers/Morning";
import Night from "containers/Night";
import Polls from "containers/Polls";
import WaitingRoom from "containers/WaitingRoom";
import React, { useState } from "react";
import styled from "styled-components";
import RulesMobile from "pages/RulesMobile";
import { StyledButton } from "components/Button/v2";
import VampirePartyTitle from "containers/Game/VampirePartyTitle";
import { gameStateType } from "core/constants";

const {
  WAITING,
  INTRO,
  READY,
  STARTED,
  HUMAN_WIN,
  VAMPIRE_WIN,
  NIGHT_INTRO,
  NIGHT,
  MORNING_INTRO,
  MORNING,
  DAY,
  POLLS,
} = gameStateType;

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
`;

const Home = ({
  socket,
  delayedProps,
  gameSession,
  onSubmitGameSession,
  onCreateSession,
  _setGameSessionValue,
  gameSessionError,
  resetGameSession,
  gameSessionId,
  _setPlayerName,
  _setPlayerNameValue,
  playerNameValue,
  playerError,
  startGame,
  startGameWithIntro,
  player,
  players = [],
  newGameAddPlayers,
  quitRound,
  // Settings props
  gameSettings,
  handleSaveSettings,
  isVIP,
  isSettingsLocked,
  isCreatingSession,
  eventLog,
}) => {
  const [modalType, setModalType] = useState(null); // 'create' | 'join' | null

  const currentEvent =
    eventLog && eventLog.length > 0 ? eventLog[eventLog.length - 1] : null;

  if (delayedProps.loading) {
    return <BatFlapping loading={delayedProps.loading} />;
  }
  if (delayedProps.preGame)
    return (
      <>
        {!delayedProps.showLoginTransition && !gameSessionId && !modalType && (
          <CenteredContent>
            <VampirePartyTitle />
            <SelectableLinkContainer>
              <StyledButton
                data-test-id="show-rules-button"
                onClick={() => setModalType("rules")}
              >
                Rules
              </StyledButton>
              <StyledButton
                data-test-id="create-game-button"
                onClick={() => setModalType("create")}
              >
                Create Room
              </StyledButton>
              <StyledButton
                data-test-id="join-game-button"
                onClick={() => setModalType("join")}
              >
                Join Game
              </StyledButton>
            </SelectableLinkContainer>
          </CenteredContent>
        )}

        {modalType === "create" && (
          <SessionNameModal
            showModal={true}
            onEnterSessionName={(name) => {
              // Create the session and navigate to it
              onCreateSession(name);
              setModalType(null);
            }}
            onClose={() => setModalType(null)}
          />
        )}
        {modalType === "rules" && (
          <RulesMobile onClickBack={() => setModalType(null)} />
        )}

        {(modalType === "join" ||
          (gameSessionId &&
            !isCreatingSession &&
            gameSession &&
            (delayedProps.validGameSession || gameSession.status !== 200))) &&
          !delayedProps.showLoginTransition && (
            <LoginForm
              onSubmitGameSession={onSubmitGameSession}
              onChangeGameSession={_setGameSessionValue}
              gameSessionError={gameSessionError}
              validGameSession={delayedProps.validGameSession}
              resetGameSession={() => {
                resetGameSession();
                setModalType(null);
              }}
              gameSessionId={gameSessionId}
              onSubmitTextInput={_setPlayerName}
              onChangeTextInput={_setPlayerNameValue}
              playerNameValue={playerNameValue}
              playerError={playerError}
            />
          )}
        {delayedProps.showLoginTransition && (
          <WaitingRoom
            playerName={delayedProps.playerNameValue}
            gameSessionId={gameSessionId}
            vipReady={delayedProps.vipReady}
            startGame={startGame}
            startGameWithIntro={startGameWithIntro}
            players={players}
            settings={gameSettings}
            onSaveSettings={handleSaveSettings}
            isVIP={isVIP}
            isSettingsLocked={isSettingsLocked}
            onVIPExit={quitRound}
            gameState={delayedProps.gameState}
          />
        )}
      </>
    );

  if (delayedProps.isIntro)
    return (
      <Intro
        player={player}
        socket={socket}
        gameState={delayedProps.isIntro}
        gameSessionId={gameSessionId}
        playerVoted={delayedProps.playerVoted}
        role={delayedProps.role}
        image={delayedProps.image}
        isExpired={delayedProps.isExpired}
      />
    );

  if (delayedProps.hasStarted)
    return (
      <CharacterInfo
        player={player}
        socket={socket}
        gameSessionId={gameSessionId}
        playerVoted={delayedProps.playerVoted}
        role={delayedProps.role}
        image={delayedProps.image}
        isExpired={delayedProps.isExpired}
      />
    );

  if (delayedProps.isNight)
    return (
      <Night
        player={player}
        socket={socket}
        gameSessionId={gameSessionId}
        playerVoted={delayedProps.playerVoted}
        message={delayedProps.message}
        role={delayedProps.role}
        image={delayedProps.image}
        isExpired={delayedProps.isExpired}
        currentEvent={currentEvent.gameState === NIGHT ? currentEvent : null}
      />
    );

  if (delayedProps.isMorning) {
    return (
      <Morning
        message={delayedProps.message}
        role={delayedProps.role}
        image={delayedProps.image}
        isExpired={delayedProps.isExpired}
        currentEvent={currentEvent.gameState === MORNING ? currentEvent : null}
      />
    );
  }

  if (delayedProps.isDay)
    return (
      <Day
        player={player}
        socket={socket}
        gameSessionId={gameSessionId}
        playerVoted={delayedProps.playerVoted}
        message={delayedProps.message}
        role={delayedProps.role}
        image={delayedProps.image}
        isExpired={delayedProps.isExpired}
      />
    );

  if (delayedProps.isPolls)
    return (
      <Polls
        message={delayedProps.message}
        role={delayedProps.role}
        image={delayedProps.image}
        isExpired={delayedProps.isExpired}
        currentEvent={currentEvent.gameState === POLLS ? currentEvent : null}
      />
    );
  if (delayedProps.humanWin || delayedProps.vampireWin)
    return (
      <EndGame
        player={player}
        message={delayedProps.message}
        role={delayedProps.role}
        image={delayedProps.image}
        isExpired={delayedProps.isExpired}
        startGame={startGame}
        startGameWithIntro={startGameWithIntro}
        newGameAddPlayers={newGameAddPlayers}
        quitRound={quitRound}
        humanWin={delayedProps.humanWin}
      />
    );

  if (delayedProps.isExpired)
    return <Expired player={player} currentEvent={currentEvent} />;
};
export default Home;
